/**
 * Knitting stitch charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const knittingStitchChartsPack: ToilePack = {
    slug: 'knitting-stitch-charts',
    category: 'craft',
    name: 'Knitting stitch charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'texture', label: 'Knit and purl', subject: 'a knitting chart of a knit-and-purl texture, seed stitch blocks alternating with basketweave, blank squares for knit and dots for purl, odd rows numbered up the right edge'},
        {key: 'lace', label: 'Lace', subject: 'a knitting lace chart of a leaf panel, circles for yarn-overs and slanted lines for decreases, the pattern repeat boxed in a heavier outline'},
        {key: 'rib', label: 'Ribbing', subject: 'a knitting chart of three ribbing patterns side by side, one-by-one, two-by-two and twisted rib, each a narrow column of symbols eight rows tall'},
    ],
    styles: [
        {key: 'symbols', label: 'Symbol grid', style: 'crisp square grid, one stitch per square, standard knitting symbols in black, row numbers up the side margins', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'bold', label: 'Bold grid', style: 'large squares with a thick grid line and big simple symbols, easy to read at a glance', palette: 'black ink only on pure white', framing: 'centered on the sheet with an even white margin all round'},
        {key: 'repeat', label: 'Tinted repeat', style: 'symbol grid with the pattern repeat tinted a pale colour and the edge stitches left white', palette: 'black symbols on pure white with the repeat tinted pale', framing: 'centered on the sheet with an even white margin all round'},
    ],
    directions: ['baby blanket', 'scarf', 'dishcloth'],
    directionHint: 'try: baby blanket',
    seo: {
        title: 'Knitting stitch charts: texture, lace, ribbing',
        metaDescription: 'Nine knitting stitch charts: a knit and purl texture, a leaf lace panel and three ribbings, in three grid styles.',
        h1: 'Knitting stitch charts: knit and purl texture, leaf lace and ribbing',
        tagline: 'Each square is one stitch, in standard knitting symbols, with row numbers up the side.',
        intro: 'Knitters follow charts for texture and lace. Each square is one stitch, read from the bottom up, and on the right side the rows run right to left. These nine cover a seed and basketweave texture, a leaf lace panel and three ribbings. Each comes as a symbol grid, a bold grid, or a grid with the repeat tinted. Type a different stitch and the same grid carries it.',
        useCases: [
            'a textured scarf',
            'a lace shawl panel',
            'a ribbed cuff',
            'a dishcloth in seed stitch',
        ],
        faq: [
            {q: 'How do I read a chart?', a: 'Start at the bottom right on the right side of the work, read each row from right to left, and follow the row numbers up the edge.'},
            {q: 'What do the blank squares and dots mean?', a: 'In the texture chart, a blank square is a knit stitch and a dot is a purl. The symbol key in your pattern is the one to follow.'},
            {q: 'Which grid is easiest to read?', a: 'The bold grid, with large squares and thick lines, is the easiest to read at a glance.'},
        ],
    },
}
